/**
 * The error envelope social-admin returns on every non-2xx response:
 *
 *   { "error": { "code": "...", "message": "...", "request_id": "...", "details": { ... } } }
 *
 * Anything that does not match (a proxy error page, an empty body) is still turned into an
 * ApiError so callers only ever handle one type.
 */
export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly details: Readonly<Record<string, unknown>>;
  readonly fieldErrors: readonly FieldError[];

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string | undefined;
    details?: Record<string, unknown> | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details ?? {};
    this.fieldErrors = readFieldErrors(this.details);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

const GENERIC_MESSAGES: Record<number, string> = {
  0: 'The server could not be reached. Check your connection and try again.',
  400: 'The request was not accepted.',
  401: 'Your session has ended. Sign in again.',
  403: 'You do not have permission to do that.',
  404: 'Not found.',
  409: 'That change conflicts with the current state. Reload and try again.',
  429: 'Too many requests. Wait a moment and try again.',
};

export function genericMessage(status: number): string {
  return (
    GENERIC_MESSAGES[status] ??
    (status >= 500 ? 'The server failed to handle the request.' : 'The request failed.')
  );
}

/** Builds an ApiError from a response body that may or may not be the standard envelope. */
export function toApiError(
  status: number,
  body: unknown,
  requestIdHeader?: string | null,
): ApiError {
  const envelope = isRecord(body) && isRecord(body.error) ? body.error : null;
  const code = envelope && typeof envelope.code === 'string' ? envelope.code : `http_${status}`;
  const message =
    envelope && typeof envelope.message === 'string' && envelope.message.trim() !== ''
      ? envelope.message
      : genericMessage(status);
  const requestId =
    envelope && typeof envelope.request_id === 'string'
      ? envelope.request_id
      : (requestIdHeader ?? undefined);
  const details = envelope && isRecord(envelope.details) ? envelope.details : undefined;
  return new ApiError({ status, code, message, requestId, details });
}

export function networkError(cause: unknown): ApiError {
  const error = new ApiError({ status: 0, code: 'network_error', message: genericMessage(0) });
  error.cause = cause;
  return error;
}

function readFieldErrors(details: Record<string, unknown>): FieldError[] {
  const fields = details.fields;
  if (!Array.isArray(fields)) return [];
  return fields.flatMap((entry): FieldError[] => {
    if (isRecord(entry) && typeof entry.field === 'string' && typeof entry.message === 'string') {
      return [{ field: entry.field, message: entry.message }];
    }
    return [];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
